import {Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {fromEvent} from 'rxjs/observable/fromEvent';
import {merge} from 'rxjs/observable/merge';
import {of} from 'rxjs/observable/of';
import {timer} from 'rxjs/observable/timer';
import {filter, finalize, startWith, switchMap, takeUntil, tap, throttleTime} from 'rxjs/operators';
import {Subject} from 'rxjs/Subject';

import {UserService} from '../api/user.service';
import {AuthGuard, hasSearchParams, LOGIN_AT, ScreenLockTimeout} from '../auth/auth.guard';
import {NotificationServiceBus} from '../notification/notification.service';
import { DISABLE_USER_TIMEOUT } from '../shared/constants';
import {GLOBAL} from '../shared/global';
import {User} from '../shared/model/user.entity';
import {SpinnerService} from '../spinner/spinner.service';
const disableUserTimeout = hasSearchParams('disable-user-test') ? 5000 : DISABLE_USER_TIMEOUT; // 15 mins

@Component({
  selector: 'app-screen-guard',
  templateUrl: './screen-guard.component.html',
  styleUrls: ['./screen-guard.component.scss'],
})
export class ScreenGuardComponent implements OnInit, OnDestroy {
  @ViewChild('eventsTarget') eventsTarget: ElementRef;

  lock$: BehaviorSubject<boolean>;

  form = new FormGroup({
    password: new FormControl('', Validators.required),
  });

  private timeUpdateExternal$ = fromEvent(window, 'storage', true).pipe(
    filter((event: StorageEvent) => event.key === LOGIN_AT)
  );

  private timeUpdate$ = new Subject<void>();

  private destroy$ = new Subject<void>();
  private disableCheckInterval: any;
  constructor(
    private router: Router,
    private spinnerService: SpinnerService,
    private userService: UserService,
    private notificationServiceBus: NotificationServiceBus,
    private ngZone: NgZone
  ) {}

  ngOnInit() {
    AuthGuard.updateSessionTime();
    this.checkUserDisabled();
    this.lock$ = new BehaviorSubject(this.isLocked());
    // logout completely when user comes after screen locked only
    if (AuthGuard.isLoggedIn() && AuthGuard.isTokenInvalid()) {
      AuthGuard.logout();
      this.router.navigate(['/authenticate/logout']);
      this.lock$.next(false);
    }

    const onUnlocked$ = this.lock$.pipe(filter((locked: boolean) => !locked));
    const onLocked$ = this.lock$.pipe(filter((locked: boolean) => locked));

    const setupLock$ = onUnlocked$.pipe(
      switchMap(() => merge(this.awakeListeners(), this.lockTimer()).pipe(takeUntil(onLocked$)))
    );

    const unlockFromExternal$ = onLocked$.pipe(
      switchMap(() => this.timeUpdateExternal$.pipe(takeUntil(onUnlocked$))),
      filter(() => !this.isLocked()),
      tap(() => this.ngZone.run(() => this.lock$.next(false)))
    );

    this.ngZone.runOutsideAngular(() => {
      merge(setupLock$, unlockFromExternal$)
        .pipe(takeUntil(this.destroy$))
        .subscribe();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  unlock() {
    this.spinnerService.requestInProcess(true);
    this.userService
      .login({email: GLOBAL.currentUser.email, password: this.form.value.password} as User)
      .pipe(
        tap(
          (res: {status: number; data: {jwt: string}}) => {
            const token = res.data.jwt;
            if (!token) {
              return;
            }

            AuthGuard.setToken(token);
            this.lock$.next(false);
            this.form.reset();
          },
          async (res: Response) => {
            let errorMessage = 'Email or Password not found';
            if (res.status === 401) {
              const body = await res.json();
              errorMessage = body.errors || errorMessage;
            }
            this.spinnerService.requestInProcess(false);
            this.notificationServiceBus.launchNotification(true, errorMessage);
          }
        ),
        finalize(() => this.spinnerService.requestInProcess(false))
      )
      .subscribe();
  }

  private isLocked() {
    return AuthGuard.isLoggedIn() && AuthGuard.isSessionExpired();
  }

  private awakeListeners() {
    const eventsToWatch = ['mousemove', 'keydown', 'touchmove'];
    return merge(...eventsToWatch.map((eventName) => fromEvent(this.eventsTarget.nativeElement, eventName, true))).pipe(
      throttleTime(3000),
      startWith(null),
      tap(() => {
        AuthGuard.updateSessionTime();
        this.resetDisableCheckInterval();
        this.timeUpdate$.next();
      })
    );
  }

  private lockTimer() {
    return merge(of(null), this.timeUpdate$, this.timeUpdateExternal$).pipe(
      switchMap(() => timer(AuthGuard.getSessionLeftTime())),
      filter(() => AuthGuard.isLoggedIn()),
      tap(() => {
        this.ngZone.run(() => this.lock$.next(true));
        AuthGuard.removeToken();
      })
    );
  }

  private getUserByEmail() {
    // while the screen guard is on, the token is invalid
    // don't check user if screen guard is on or the user is not logged in
    if (AuthGuard.isLoggedIn() && !AuthGuard.isTokenInvalid()) {
      this.userService.getUserByEmail(GLOBAL.currentUser.email)
        .pipe(
          tap(
            // for a response, don't do anything and let the disable check run in its next interval
            (_) => {},
            (err: Response) => {
              if (err.status === 401) {
                AuthGuard.logout();
                clearInterval(this.disableCheckInterval);
                this.router.navigate(['/authenticate/logout']);
              }
            }
          ),
        ).subscribe();
    } else {
      this.resetDisableCheckInterval();
    }
  }

  // this function run every 15 min to check if the user is disabled
  // 15 min time will be reset if user perform some action
  private checkUserDisabled() {
   this.disableCheckInterval = setInterval(() => this.getUserByEmail(), disableUserTimeout);
  }

  // clear previously running interval and start a new one
  private resetDisableCheckInterval() {
    clearInterval(this.disableCheckInterval);
    this.checkUserDisabled();
  }
}




// WEBPACK FOOTER //
// ./src/app/screen-guard/screen-guard.component.ts